import { supabase } from '../lib/supabase';
import { storage } from '../lib/storage';

/**
 * Service for generating images using the backend API
 */
export class ImageGenerationService {
  /**
   * Generate an image from a text prompt
   * @param prompt Text description to generate image from
   * @param options Configuration options
   * @returns Promise with the generated image URL
   */
  static async generateImage(prompt: string, options: {
    width?: number;
    height?: number;
    enhancePrompt?: boolean;
  } = {}): Promise<string> {
    try {
      // Get auth token from Supabase session
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const userId = sessionData.session?.user?.id;
      
      if (!token || !userId) {
        throw new Error('Authentication required to generate images');
      }
      
      // Default options
      const {
        width = 1024,
        height = 1024,
        enhancePrompt = true,
      } = options;
      
      // Make API request
      const response = await fetch('/api/marketing/text-to-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt,
          width,
          height,
          enhancePrompt
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error || 
          `Image generation failed with status: ${response.status}`
        );
      }
      
      const responseData = await response.json();
      const imageUrl: string | undefined = responseData?.imageUrl;
      if (!imageUrl) {
        throw new Error('Image generation succeeded but no image URL was returned');
      }

      try {
        const remoteResponse = await fetch(imageUrl, { mode: 'cors' });
        if (!remoteResponse.ok) {
          throw new Error(`Failed to download generated image (${remoteResponse.status})`);
        }

        const blob = await remoteResponse.blob();
        const mimeType = blob.type || 'image/png';
        const extension = mimeType.split('/')[1] || 'png';
        const fileName = `image-generator-${Date.now()}.${extension}`;
        const file = new File([blob], fileName, { type: mimeType });
        const path = `image-generator/${userId}/${fileName}`;
        const upload = await storage.uploadFile({
          file,
          path,
          metadata: {
            sourceModule: 'image-generator',
            docType: 'image',
            visibility: 'private',
            tags: ['image-generator', prompt].filter(Boolean),
            prompt,
            width,
            height,
          },
        });

        const signedUrl = await storage.getSignedUrl(upload.key, 600);
        return signedUrl;
      } catch (assetError) {
        console.error('Failed to persist generated image to storage:', assetError);
        return imageUrl;
      }
    } catch (error) {
      console.error('Error generating image:', error);
      throw error;
    }
  }
}
